import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, usePoll } from "@/lib/api";
import { FundingReadyBadge } from "@/components/funding/FundingReadyBadge";
import { SponsorEmails } from "@/components/funding/SponsorEmails";
import { toast } from "sonner";

const TYPES = ["SBA 7(a) loan", "SBA Microloan", "SBA 504 loan", "Conventional bank loan", "Business line of credit"];
const STATUS = { ready: ["Included", "text-emerald-700"], on_file: ["On file", "text-emerald-700"], needed: ["Still needed", "text-amber-700"] };

export function Viewer({ p }) {
  const pdf = async () => {
    const r = await api.get(`/funding/bank/${p.package_id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${p.title}.pdf`; a.click();
  };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6" data-testid="bank-viewer">
      <div className="xl:col-span-2 bg-white border border-slate-200 rounded-lg p-6 prose-gv max-w-none" data-testid="bank-content">
        {p.status === "generating" && <p className="text-emerald-700"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" />Writing your document… about 1–2 minutes.</p>}
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.content || ""}</ReactMarkdown>
      </div>
      <div className="space-y-4">
        <FundingReadyBadge ready={p.funding_ready} testid="package-funding-ready-badge" />
        <Button onClick={pdf} disabled={p.status !== "ready"} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="bank-pdf-button"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
        {p.track === "sponsorship" && <SponsorEmails key={p.package_id} p={p} />}
        {p.checklist && <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="bank-checklist">
          <h4 className="font-semibold text-slate-900 text-sm">Lender document checklist</h4>
          <ul className="mt-2 space-y-1.5 text-xs">{p.checklist.map((c) => <li key={c.item} className="flex justify-between gap-2"><span className="text-slate-600">{c.item}</span><span className={`font-semibold shrink-0 ${STATUS[c.status][1]}`}>{STATUS[c.status][0]}</span></li>)}</ul>
        </div>}
      </div>
    </div>
  );
}

export function BankPackage() {
  const { hasPkg } = useAuth();
  const allowed = hasPkg("proposal_writer") || hasPkg("licensing_guide");
  const [f, setF] = useState({ loan_type: TYPES[0], loan_amount: "", use_of_funds: "", owner_equity: "", collateral: "", notes: "" });
  const [list, setList] = useState([]);
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = useCallback((id) => api.get(`/funding/bank/${id}`).then((r) => setCur(r.data)), []);
  useEffect(() => { if (allowed) api.get("/funding/bank").then((r) => { setList(r.data); if (r.data[0]) open(r.data[0].package_id); }); }, [allowed, open]);
  const poll = useCallback(() => cur && open(cur.package_id), [cur, open]);
  usePoll(poll, cur?.status === "generating", 3000);
  if (!allowed) return <UpgradeGate pkg="proposal_writer" title="Unlock the Bank / SBA package">A lender-ready business plan with a Compliance &amp; Governance section built from your verified requirements, plus the SBA document checklist. Included with any paid plan.</UpgradeGate>;
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post("/funding/bank", { ...f, loan_amount: Number(f.loan_amount), owner_equity: Number(f.owner_equity || 0) });
      await open(r.data.package_id); api.get("/funding/bank").then((x) => setList(x.data));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-lg p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bank-form">
        <select value={f.loan_type} onChange={set("loan_type")} className="h-9 rounded-md border border-slate-200 px-3 text-sm" data-testid="bank-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <Input type="number" min="1" placeholder="Loan amount ($)" value={f.loan_amount} onChange={set("loan_amount")} data-testid="bank-amount-input" />
        <Textarea rows={2} placeholder="Use of funds, e.g. $60,000 lifts and diagnostic equipment, $20,000 working capital" value={f.use_of_funds} onChange={set("use_of_funds")} className="md:col-span-2" data-testid="bank-use-input" />
        <Input type="number" min="0" placeholder="Your cash put in ($)" value={f.owner_equity} onChange={set("owner_equity")} data-testid="bank-equity-input" />
        <Input placeholder="Collateral (optional)" value={f.collateral} onChange={set("collateral")} data-testid="bank-collateral-input" />
        <Textarea rows={2} placeholder="Anything else the lender should know (optional)" value={f.notes} onChange={set("notes")} className="md:col-span-2" data-testid="bank-notes-input" />
        <Button onClick={go} disabled={busy || !(Number(f.loan_amount) > 0) || f.use_of_funds.length < 5} className="bg-emerald-600 hover:bg-emerald-700 md:col-span-2" data-testid="bank-generate-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Build my lender package</Button>
        <p className="text-xs text-slate-400 md:col-span-2">Uses your Company profile, your latest "Can I open this?" check, your licensing roadmap and your Document Vault.</p>
      </div>
      {list.length > 1 && <div className="flex flex-wrap gap-2">{list.map((p) => <button key={p.package_id} onClick={() => open(p.package_id)} className={`text-xs px-3 py-1.5 rounded-full border ${cur?.package_id === p.package_id ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`bank-tab-${p.package_id}`}><FileText className="inline h-3 w-3 mr-1" />{p.title}</button>)}</div>}
      {cur && <Viewer p={cur} />}
    </div>
  );
}
